/** Elevated role + RBAC permission helpers for menus and routes. */

export function isSuperAdmin(user) {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  const clinic = (user.clinic_role || '').toLowerCase();
  return role === 'super_admin' || clinic === 'super_admin' || clinic === 'director' || clinic === 'dean_admin';
}

/** FIC / Director / Dean / management role — clinic admin before permissions hydrate. */
export function isLeadershipAdmin(user) {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  if (role === 'management') return true;
  const clinic = (user.clinic_role || '').toLowerCase();
  return clinic === 'fic' || clinic === 'director' || clinic === 'dean_admin';
}

export function isTenantAdmin(user) {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  if (role === 'admin' || role === 'management' || isSuperAdmin(user) || isLeadershipAdmin(user)) {
    return true;
  }
  return hasAnyPermission(user, [
    'admin.users', 'admin.roles', 'admin.permissions', 'admin.specialisations',
    'roster.manage', 'roster.view', 'management.view',
  ]);
}

export function hasFullAccess(user) {
  return isSuperAdmin(user) || (user?.role || '').toLowerCase() === 'admin';
}

export function userPermissions(user) {
  const list = user?.permissions;
  return Array.isArray(list) ? list : [];
}

export function hasPermission(user, code) {
  if (!user || !code) return false;
  if (hasFullAccess(user)) return true;
  if (isLeadershipAdmin(user) && (
    code.startsWith('admin.') || code.startsWith('roster.')
    || code === 'audit.auth.view' || code === 'management.view' || code === 'reports.view'
  )) {
    return true;
  }
  return userPermissions(user).includes(code);
}

export function hasAnyPermission(user, codes) {
  if (!codes?.length) return false;
  if (hasFullAccess(user)) return true;
  return codes.some((c) => hasPermission(user, c));
}

export function canAccessRole(user, allowedRoles) {
  if (!allowedRoles || !allowedRoles.length) return true;
  if (hasFullAccess(user)) return true;
  return allowedRoles.includes(user?.role || 'doctor');
}

/** Role list OR any of the permission codes. */
export function canAccessRoute(user, { allowedRoles, anyPermission } = {}) {
  if (!user) return false;
  if (hasFullAccess(user)) return true;
  if (anyPermission?.length && hasAnyPermission(user, anyPermission)) return true;
  if (allowedRoles?.length && canAccessRole(user, allowedRoles)) return true;
  if (isLeadershipAdmin(user) && anyPermission?.length) return true;
  if (!allowedRoles?.length && !anyPermission?.length) return true;
  return false;
}
